"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, CircleCheck as CheckCircle2, ArrowRight, Target, ChartBar as BarChart3, Quote, Building2, MapPin, Sparkles, Search } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const caseStudiesData = [{
  id: "case-1",
  client: "Regional Commercial Services",
  location: "Swindon & Wiltshire, UK",
  category: "Local & Regional SEO",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_local_seo.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_local_seo.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_local_seo.png-wvc-sizes",
  altText: "SEO growth analytics dashboard showing increased organic search traffic",
  primaryMetric: "+185%",
  primaryMetricLabel: "Organic Search Traffic",
  secondaryMetric: "+240%",
  secondaryMetricLabel: "Map Pack Impressions",
  challenge: "High competition from national operators with poor regional search visibility and minimal organic lead inquiries.",
  strategy: "Detailed technical site overhaul, hyper-targeted local landing page clusters, and Google Business Profile schema optimization.",
  implementation: "Structured entity data, streamlined crawl budget, localized content silo strategy, and clean citation building.",
  results: "Achieved top 3 local pack rankings across 14 key regional commercial terms within 6 months.",
  businessImpact: "Generated 45+ qualified direct commercial inquiries per month, reducing paid ad reliance significantly.",
  testimonial: "Working directly with Yassine transformed our website into our primary lead generator. Clear strategy without agency jargon.",
  clientName: "David M.",
  clientRole: "Managing Director"
}, {
  id: "case-2",
  client: "UK B2B Professional Consultancy",
  location: "National UK Market",
  category: "B2B SEO & Lead Gen",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_b2b_seo.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_b2b_seo.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_b2b_seo.png-wvc-sizes",
  altText: "B2B search ranking report showing page 1 positioning",
  primaryMetric: "+310%",
  primaryMetricLabel: "Page 1 Commercial Keywords",
  secondaryMetric: "+140%",
  secondaryMetricLabel: "Qualified Inquiries",
  challenge: "Stagnant organic rankings, outranked by larger corporate agencies on high-intent commercial keywords.",
  strategy: "Full search intent mapping, technical audit addressing indexation bloat, and authoritative topical hub creation.",
  implementation: "Service page UX restructuring, technical content optimization, internal link flow re-engineering, and outreach.",
  results: "Ranked #1-#3 for high-value national B2B search terms across targeted core services.",
  businessImpact: "Added over GBP 120k in estimated pipeline value through organic inbound inquiries in 9 months.",
  testimonial: "Direct access to Yassine's hands-on expertise made all the difference. Measurable revenue results and complete transparency.",
  clientName: "Sarah T.",
  clientRole: "Marketing Director"
}];
export default function CaseStudyImpact() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState<string>("case-1");
  const activeCase = caseStudiesData.find(item => item.id === activeTab) || caseStudiesData[0];
  return <section id="casestudyimpact" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 transition-colors">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <Badge variant="outline" className="mb-4 border-primary/20 text-primary bg-primary/5 px-3 py-1 font-medium">
            <Sparkles className="w-3.5 h-3.5 mr-1.5 text-accent" /> Proven Real-World Results
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-serif">
            Case Studies & Business Impact
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Real outcomes from direct, hands-on SEO strategic execution. Clear challenges, methodical strategy, and transparent, verified measurement.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {caseStudiesData.map((item, i) => {
          const isActive = item.id === activeTab;
          return <div key={item.id} data-index={i}>
                <Button variant={isActive ? "default" : "outline"} size="lg" onClick={() => setActiveTab(item.id)} className={`rounded-lg transition-all ${isActive ? "bg-primary text-primary-foreground shadow-md" : "bg-card text-foreground hover:bg-muted border-border"}`}>
                  <Building2 className="w-4 h-4 mr-2" />
                  <span>{item.client}</span>
                  <Badge variant="secondary" className="ml-2 text-xs bg-secondary text-secondary-foreground hidden sm:inline-flex">
                    {item.category}
                  </Badge>
                </Button>
              </div>;
        })}
        </div>

        <motion.div key={activeCase.id} initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        ease: "easeOut"
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7 space-y-6">
            <Card className="border border-border bg-card shadow-sm overflow-hidden">
              <CardHeader className="p-6 sm:p-8 bg-muted/30 border-b border-border">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <Badge variant="default" className="bg-primary text-primary-foreground mb-2">
                      {activeCase.category}
                    </Badge>
                    <CardTitle className="text-2xl sm:text-3xl font-bold font-serif text-foreground">
                      {activeCase.client}
                    </CardTitle>
                    <div className="flex items-center text-sm text-muted-foreground mt-1">
                      <MapPin className="w-4 h-4 mr-1 text-accent" />
                      <span>{activeCase.location}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 bg-background p-3 rounded-lg border border-border shadow-xs">
                    <div className="text-center">
                      <div className="text-2xl sm:text-3xl font-extrabold text-primary font-serif">
                        {activeCase.primaryMetric}
                      </div>
                      <div className="text-xs font-medium text-muted-foreground">
                        {activeCase.primaryMetricLabel}
                      </div>
                    </div>
                    <Separator orientation="vertical" className="h-8" />
                    <div className="text-center">
                      <div className="text-2xl sm:text-3xl font-extrabold text-accent font-serif">
                        {activeCase.secondaryMetric}
                      </div>
                      <div className="text-xs font-medium text-muted-foreground">
                        {activeCase.secondaryMetricLabel}
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-6 sm:p-8 space-y-6 font-sans">
                <div>
                  <div className="flex items-center gap-2 text-base font-semibold text-foreground mb-2">
                    <Target className="w-5 h-5 text-primary shrink-0" />
                    <h3>The Challenge</h3>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed pl-7">
                    {activeCase.challenge}
                  </p>
                </div>

                <Separator />

                <div>
                  <div className="flex items-center gap-2 text-base font-semibold text-foreground mb-2">
                    <Search className="w-5 h-5 text-primary shrink-0" />
                    <h3>SEO Strategy</h3>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed pl-7">
                    {activeCase.strategy}
                  </p>
                </div>

                <Separator />

                <div>
                  <div className="flex items-center gap-2 text-base font-semibold text-foreground mb-2">
                    <BarChart3 className="w-5 h-5 text-primary shrink-0" />
                    <h3>Practical Implementation</h3>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed pl-7">
                    {activeCase.implementation}
                  </p>
                </div>

                <Separator />

                <div className="bg-primary/5 p-4 sm:p-5 rounded-lg border border-primary/10">
                  <div className="flex items-center gap-2 text-base font-semibold text-foreground mb-2">
                    <TrendingUp className="w-5 h-5 text-primary shrink-0" />
                    <h3>Verified Results & Business Impact</h3>
                  </div>
                  <ul className="space-y-2 pl-2 mt-3">
                    <li className="flex items-start text-sm text-foreground">
                      <CheckCircle2 className="w-4 h-4 text-primary mr-2.5 mt-0.5 shrink-0" />
                      <span><strong>Ranking Achievement:</strong> {activeCase.results}</span>
                    </li>
                    <li className="flex items-start text-sm text-foreground">
                      <CheckCircle2 className="w-4 h-4 text-primary mr-2.5 mt-0.5 shrink-0" />
                      <span><strong>Commercial Impact:</strong> {activeCase.businessImpact}</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-5 space-y-6">
            <Card className="border border-border bg-card shadow-sm overflow-hidden">
              <div className="relative aspect-video sm:aspect-4/3 w-full overflow-hidden bg-muted">
                <img src={activeCase.image} data-wvc-srcset={activeCase.imageSrcset} data-wvc-sizes={activeCase.imageSizes} alt={activeCase.altText} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-4">
                  <span className="text-white text-xs font-medium px-2.5 py-1 rounded-md bg-black/40 backdrop-blur-xs">
                    Direct Campaign Data
                  </span>
                </div>
              </div>
            </Card>

            <Card className="border border-border bg-card shadow-sm p-6 relative">
              <Quote className="w-8 h-8 text-primary/20 absolute top-4 right-4" />
              <div className="space-y-4">
                <p className="text-foreground text-sm italic font-sans leading-relaxed">
                  "{activeCase.testimonial}"
                </p>
                <div className="flex items-center gap-3 pt-2">
                  <SafeHydrate><Avatar className="h-10 w-10 border border-primary/20">
                    <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/client_avatar_placeholder.png" alt={activeCase.clientName} />
                    <AvatarFallback className="bg-primary/10 text-primary font-bold">
                      {activeCase.clientName.charAt(0)}
                    </AvatarFallback>
                  </Avatar></SafeHydrate>
                  <div>
                    <div className="text-sm font-semibold text-foreground font-serif">
                      {activeCase.clientName}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {activeCase.clientRole}, {activeCase.client}
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="border border-primary/20 bg-muted/40 p-6 sm:p-8 text-center space-y-4">
              <h3 className="text-xl font-bold font-serif text-foreground">
                Ready for Similar Search Growth?
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Work directly with Yassine Essaadi. No junior account managers, no template audits, just tailored SEO strategy.
              </p>
              <div className="pt-2 flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto">
                  <Link to="/ContactPage">
                    Book a Free SEO Consultation
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                  <Link to="/CaseStudiesPage">
                    View All Case Studies
                  </Link>
                </Button>
              </div>
            </Card>
          </div>
        </motion.div>
      </div>
    </section>;
}